import { Card, Col, Row, Space } from 'antd';
import { useField } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import { type CreateDealerMutationVariables } from '../../../../api/mutations/createDealer';
import { useListCompaniesForSelectionQuery } from '../../../../api/queries/listCompaniesForSelection';
import { type DealerSocialMediaInput } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import InputField from '../../../../components/fields/InputField';
import SelectField from '../../../../components/fields/SelectField';
import SwitchField from '../../../../components/fields/SwitchField';
import TranslatedInputField from '../../../../components/fields/TranslatedInputField';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import MarketTypeField from '../../../shared/MarketTypeField';
import DealerContact from './DealerContact';
import DealerIntegrationDetails from './DealerIntegrationDetails';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type FormValues = CreateDealerMutationVariables & {
    socialMedia: Array<DealerSocialMediaInput & { icon?: File | UploadFileWithPreviewFormDataFragment; id?: string }>;
};

export const defaultValue: FormValues = {
    companyId: '',
    dealerInput: {
        displayName: '',
        coe: 0,
        ppsr: 0,
        estFee: 0,
        legalName: { defaultValue: '', overrides: [] },
        isActive: false,
        contact: {
            telephone: { prefix: null, value: '' },
            email: '',
            address: { defaultValue: '', overrides: [] },
            additionalInfo: { defaultValue: '', overrides: [] },
        },
        integrationDetails: {
            dealerCode: '',
            partnerNumber: '',
            assortment: '',
            additionalParameter: [{ purpose: '', value: '' }],
        },
        disclaimers: {
            financingDisclaimer: [],
            insuranceDisclaimer: [],
        },
    },
    socialMedia: [{ title: { defaultValue: '', overrides: [] }, content: '' }],
};
const DealerForm = () => {
    const { t } = useTranslation('dealerForm');

    // get company context
    const company = useCompany(true);

    const { yesNoSwitch } = useSystemSwitchData();

    const { data } = useListCompaniesForSelectionQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id },
        },
    });
    const companies = useMemo(() => data?.list?.items || [], [data?.list?.items]);
    const companyList = useMemo(
        () => companies.map(company => ({ label: company.displayName, value: company.id })),
        [companies]
    );

    const [{ value: companyId }] = useField('companyId');

    const dealerCompany = companies.find(i => i.id === companyId);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Card title={t('dealerForm:mainDetailsTab')}>
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <InputField
                            {...t('dealerForm:fields.dealerInput.displayName', { returnObjects: true })}
                            name="dealerInput.displayName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <TranslatedInputField
                            {...t('dealerForm:fields.dealerInput.legalName', { returnObjects: true })}
                            name="dealerInput.legalName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <SelectField
                            {...t('dealerForm:fields.company', { returnObjects: true })}
                            name="companyId"
                            options={companyList}
                            required
                            showSearch
                        />
                    </Col>
                    <MarketTypeField
                        companyId={dealerCompany?.id}
                        countryCode={dealerCompany?.countryCode}
                        prefix="dealerInput"
                    />
                    <Col {...colSpan}>
                        <SwitchField
                            {...t('dealerForm:fields.dealerInput.isActive', { returnObjects: true })}
                            name="dealerInput.isActive"
                            {...yesNoSwitch}
                        />
                    </Col>
                </Row>
            </Card>
            <DealerContact />
            <DealerIntegrationDetails hasAutoplay={company?.availableModules.hasAutoplayModules} />
        </Space>
    );
};

export default DealerForm;
